import { useState, useCallback, useMemo } from 'react'

/**
 * Hook useValidate
 * @param {object} initialValues - Initial values of the object.
 * @param {object} validationRules - Validation rules as key-value pairs.
 * @returns {object} { values, errors, isValid, dirtyValues, isDirty, validate, handleChange, resetErrors, setError, setErrors, resetValues, resetField, resetDirtyValues,
    hasAnyFieldChanged }
 */
export function useValidate(
  initialValues,
  validationRules,
  options = { handleChangeFunc: null, ignoreDirtyValues: [] },
) {
  const [initialValuesState] = useState(initialValues)
  const [innerValues, setInnerValues] = useState(initialValues)
  const [errors, setErrors] = useState({})
  const [dirtyValues, setDirtyValues] = useState(new Set())

  const getPropertyName = useCallback((obj) => Object.keys(obj)[0], [])

  // Set errors for a single field
  const setError = useCallback((field, errorMessage) => {
    setErrors((prev) => ({ ...prev, [field]: errorMessage }))
  }, [])

  // Reset a single field
  const resetField = useCallback((field) => {
    setInnerValues((prev) => ({ ...prev, [field]: '' }))
    setDirtyValues((prev) => new Set([...prev, field]))
  }, [])

  // Reset all fields
  const resetValues = useCallback(() => {
    setInnerValues(initialValuesState)
    setDirtyValues(new Set())
  }, [initialValuesState])

  // Reset all errors
  const resetErrors = useCallback(() => {
    setErrors({})
  }, [])

  const resetDirtyValues = useCallback(() => {
    setDirtyValues(new Set())
  }, [])

  // Validate a single field
  const validateField = useCallback(
    (field, value) => {
      const rule = validationRules[field]
      if (!rule) return null

      const errorMessage = rule(value)
      setErrors((prev) => ({ ...prev, [field]: errorMessage }))
      return errorMessage
    },
    [validationRules],
  )

  // Validate all fields
  const validate = useCallback(() => {
    let isValid = true
    const newErrors = Object.keys(validationRules).reduce((acc, field) => {
      const error = validateField(field, innerValues[field])
      if (error) isValid = false
      acc[field] = error
      return acc
    }, {})

    setErrors(newErrors)
    return isValid
  }, [validateField, innerValues, validationRules])

  // Handle field change
  const handleChange = useCallback(
    (obj) => {
      const field = getPropertyName(obj)
      const value = obj[field]

      setInnerValues((prev) => ({ ...prev, [field]: value }))
      validateField(field, value)

      if (!options?.ignoreDirtyValues?.includes(field)) {
        setDirtyValues((prev) => new Set([...prev, field]))
      }

      if (options?.handleChangeFunc) {
        options.handleChangeFunc(field, value)
      }
    },
    [validateField, getPropertyName, options],
  )
  // Set values
  const setValues = useCallback(
    (values) => {
      setInnerValues(values)
      setDirtyValues(new Set())
      resetErrors()
    },
    [resetErrors],
  )

  const hasAnyFieldChanged = useMemo(
    () => Object.keys(initialValuesState).some((key) => innerValues[key] !== initialValuesState[key]),
    [innerValues, initialValuesState],
  )

  // Check if the entire form is valid
  const isValid = useMemo(() => Object.values(errors).every((error) => !error), [errors])

  // Check if the fields are dirty
  const isDirty = useMemo(() => dirtyValues.size > 0, [dirtyValues])

  return {
    values: innerValues,
    errors,
    isValid,
    dirtyValues,
    isDirty,
    validate,
    handleChange,
    resetErrors,
    setError,
    setErrors,
    setValues,
    resetValues,
    resetField,
    resetDirtyValues,
    hasAnyFieldChanged,
  }
}
